import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { X, Loader2, Save } from "lucide-react";
import { fmtEUR2, fmtPct, fmtDateIT } from "@/lib/format";
import { toast } from "sonner";

export function CalculatorsDialog({ open, type, onClose, client, onSavedAsUpdate }) {
  if (!open || !type) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-[2px] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white shadow-[0_24px_64px_rgba(10,25,47,0.18)] w-full max-w-2xl max-h-[92vh] overflow-y-auto" style={{ border: "1px solid #E8ECF2", borderRadius: 6 }} data-testid={`calc-${type}-dialog`}>
        <div className="sticky top-0 bg-white px-6 py-5 flex items-center justify-between" style={{ borderBottom: "1px solid #F0F2F5" }}>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: "var(--gold)" }}>Calcolatore</div>
            <h3 className="font-serif text-2xl text-slate-900 font-light">
              {type === "dpr" ? "Calcolo DPR" : type === "loan" ? "Calcolatore Prestito" : "Debito Residuo"}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">
          {type === "dpr" && <DprForm client={client} onSavedAsUpdate={onSavedAsUpdate} />}
          {type === "loan" && <LoanForm onSavedAsUpdate={onSavedAsUpdate} />}
          {type === "residual" && <ResidualForm onSavedAsUpdate={onSavedAsUpdate} />}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, gold = false }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#F0F2F5] last:border-0">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-sm tabular-nums font-semibold ${gold ? "text-[#8a6e3d]" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function DprForm({ client, onSavedAsUpdate }) {
  const [startCqs, setStartCqs] = useState(client?.dpr || "");
  const [duration, setDuration] = useState(120);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const calc = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/calc/dpr", { startCqs, durationMonths: Number(duration) });
      setRes(r.data);
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };
  const saveAsUpdate = () => {
    if (!res) return;
    onSavedAsUpdate?.(`📅 DPR (${duration}m da ${fmtDateIT(startCqs)}): DPR ${fmtDateIT(res.dpr)} • Delibera ${fmtDateIT(res.delibera)} • Istruttoria ${fmtDateIT(res.istruttoria)}`);
    toast.success("Salvato come aggiornamento");
  };
  return (
    <form onSubmit={calc} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Inizio CQS</label>
          <input type="date" required value={startCqs} onChange={(e) => setStartCqs(e.target.value)} data-testid="dpr-start" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Durata (mesi)</label>
          <select value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="dpr-dur" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-sm">
            {[24,36,48,60,72,84,96,108,120].map((m) => <option key={m}>{m}</option>)}
          </select>
        </div>
      </div>
      <button type="submit" disabled={loading} data-testid="dpr-submit" className="w-full text-white py-2 rounded-sm flex items-center justify-center gap-2" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />} Calcola
      </button>
      {res && (
        <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-sm">
          <Row label="DPR" value={fmtDateIT(res.dpr)} gold />
          <Row label="Delibera" value={fmtDateIT(res.delibera)} />
          <Row label="Istruttoria" value={fmtDateIT(res.istruttoria)} />
          <Row label="Firma" value={fmtDateIT(res.firma)} />
          <Row label="Scadenza CRM" value={fmtDateIT(res.scadenzaCrm)} />
          {onSavedAsUpdate && <button type="button" onClick={saveAsUpdate} className="mt-3 text-xs flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 rounded-sm hover:bg-white"><Save className="w-3 h-3" /> Salva come aggiornamento</button>}
        </div>
      )}
    </form>
  );
}

function LoanForm({ onSavedAsUpdate }) {
  const [v, setV] = useState({ net1: 0, mens1: 13, net2: 0, mens2: 13, extra1: 0, extra2: 0, impegniAttuali: 0, estinguere: 0, altreTrattenute: 0, margine20Pct: true, rataNuova: 0, sogliaSostenibile: 35, durataStimataMesi: 120, tanStimato: 9 });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const calc = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...v };
      Object.keys(payload).forEach((k) => { if (typeof payload[k] !== "boolean") payload[k] = Number(payload[k] || 0); });
      const r = await api.post("/calc/loan", payload);
      setRes(r.data);
    } catch { toast.error("Errore calcolo"); }
    finally { setLoading(false); }
  };

  const saveAsUpdate = () => {
    if (!res) return;
    onSavedAsUpdate?.(`💼 Calc Prestito: Disp ${fmtEUR2(res.disponibilita)} • Rapporto ${fmtPct(res.rapportoRataDispPct)} • Capitale stim. ${fmtEUR2(res.capitaleStimato)}`);
    toast.success("Salvato");
  };

  const fields = [
    ["net1","Reddito netto 1"],["mens1","Mensilità 1"],["net2","Reddito netto 2"],["mens2","Mensilità 2"],
    ["extra1","Extra mensile 1"],["extra2","Extra mensile 2"],["impegniAttuali","Rate attuali"],["estinguere","Da estinguere"],
    ["altreTrattenute","Altre trattenute"],["rataNuova","Rata proposta"],["sogliaSostenibile","Soglia sostenibile %"],
    ["durataStimataMesi","Durata stim. (mesi)"],["tanStimato","TAN stim. %"],
  ];
  return (
    <form onSubmit={calc}>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {fields.map(([k, lbl]) => (
          <div key={k}>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">{lbl}</label>
            <input type="number" step="0.01" value={v[k]} onChange={(e) => set(k, e.target.value)} data-testid={`loan-${k}`} className="w-full mt-1 px-2 py-2 text-sm border border-slate-300 rounded-sm tabular-nums" />
          </div>
        ))}
      </div>
      <label className="flex items-center gap-2 mt-3 text-sm text-slate-700">
        <input type="checkbox" checked={v.margine20Pct} onChange={(e) => set("margine20Pct", e.target.checked)} /> Applica margine sicurezza 20%
      </label>
      <button type="submit" disabled={loading} data-testid="loan-submit" className="mt-4 w-full text-white py-2 rounded-sm flex items-center justify-center gap-2" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />} Calcola
      </button>
      {res && (
        <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-sm">
          <Row label="Reddito mensile" value={fmtEUR2(res.redditoMensile)} />
          <Row label="Margine 20%" value={fmtEUR2(res.margineSicurezza20)} />
          <Row label="Disponibilità" value={fmtEUR2(res.disponibilita)} gold />
          <Row label="Rapporto rata/disp." value={fmtPct(res.rapportoRataDispPct)} />
          <Row label="Margine dopo nuova rata" value={fmtEUR2(res.margineDopoNuovaRata)} />
          <Row label="Risparmio estinzioni" value={fmtEUR2(res.risparmioEstinzioni)} />
          <Row label="Rata massima" value={fmtEUR2(res.rataMassimaSostenibile)} />
          <Row label="Capitale stimato" value={fmtEUR2(res.capitaleStimato)} gold />
          {onSavedAsUpdate && <button type="button" onClick={saveAsUpdate} className="mt-3 text-xs flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 rounded-sm hover:bg-white"><Save className="w-3 h-3" /> Salva come aggiornamento</button>}
        </div>
      )}
    </form>
  );
}

function ResidualForm({ onSavedAsUpdate }) {
  const [v, setV] = useState({ rata: 0, durataMesi: 120, tan: 9, startDate: "", asOfDate: new Date().toISOString().slice(0, 10) });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const calc = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/calc/residual", { ...v, rata: Number(v.rata), durataMesi: Number(v.durataMesi), tan: Number(v.tan) });
      setRes(r.data);
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };

  const saveAsUpdate = () => {
    if (!res) return;
    onSavedAsUpdate?.(`💰 Debito residuo al ${fmtDateIT(v.asOfDate)}: ${fmtEUR2(res.debitoResiduo)} (rate pagate ${res.ratePagate}/${res.ratePagate + res.rateResidue})`);
    toast.success("Salvato");
  };

  const fieldCls = "w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-sm tabular-nums focus:outline-none focus:border-[#B7955B] focus:ring-1 focus:ring-[#B7955B]/30";
  const labelCls = "block text-[10px] uppercase tracking-wider text-slate-500 font-semibold";

  return (
    <form onSubmit={calc} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>Rata mensile (€)</label>
          <input type="number" step="0.01" required value={v.rata}
            onChange={(e) => set("rata", e.target.value)} data-testid="rd-rata"
            placeholder="es. 250.00" className={fieldCls} />
        </div>
        <div>
          <label className={labelCls}>Durata totale (mesi)</label>
          <input type="number" required value={v.durataMesi}
            onChange={(e) => set("durataMesi", e.target.value)} data-testid="rd-dur"
            placeholder="es. 120" className={fieldCls} />
        </div>
        <div>
          <label className={labelCls}>TAN %</label>
          <input type="number" step="0.01" required value={v.tan}
            onChange={(e) => set("tan", e.target.value)} data-testid="rd-tan"
            placeholder="es. 9.0" className={fieldCls} />
        </div>
        <div>
          <label className={labelCls}>Data inizio finanziamento</label>
          <input type="date" required value={v.startDate}
            onChange={(e) => set("startDate", e.target.value)} data-testid="rd-start"
            className={fieldCls} />
        </div>
        <div>
          <label className={labelCls}>Data di riferimento</label>
          <input type="date" required value={v.asOfDate}
            onChange={(e) => set("asOfDate", e.target.value)} data-testid="rd-asof"
            className={fieldCls} />
        </div>
      </div>
      <p className="text-[11px] text-slate-500 italic leading-snug">
        Inserisci i dati del finanziamento. Il debito residuo viene calcolato col metodo dell'ammortamento francese (rata costante) alla data di riferimento.
      </p>
      <button type="submit" disabled={loading} data-testid="rd-submit"
        className="w-full text-white py-2.5 rounded-sm flex items-center justify-center gap-2 font-semibold text-sm"
        style={{ background: "var(--navy)" }}
        onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"}
        onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />} Calcola debito residuo
      </button>
      {res && (
        <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-sm">
          <Row label="Capitale iniziale" value={fmtEUR2(res.capitaleIniziale)} />
          <Row label="Rate pagate" value={`${res.ratePagate} / ${res.ratePagate + res.rateResidue}`} />
          <Row label="Pagato (capitale)" value={fmtEUR2(res.pagatoCapitale)} />
          <Row label="Pagato totale" value={fmtEUR2(res.pagatoTotale)} />
          <Row label="Interessi pagati" value={fmtEUR2(res.interessiPagati)} />
          <Row label="Debito residuo" value={fmtEUR2(res.debitoResiduo)} gold />
          {onSavedAsUpdate && (
            <button type="button" onClick={saveAsUpdate}
              className="mt-3 text-xs flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 rounded-sm hover:bg-white">
              <Save className="w-3 h-3" /> Salva come aggiornamento
            </button>
          )}
        </div>
      )}
    </form>
  );
}
